import { z } from 'zod';

// API Response Schemas
export const apiStatusSchema = z.object({
  code: z.number(),
  message: z.string()
});

export const connectResponseSchema = z.object({
  status: apiStatusSchema,
  spinwheelId: z.string().optional(),
  hubspotRecordId: z.string().optional()
});

export const createContactResponseSchema = z.object({
  spinwheelStatus: z.enum(['QUALIFIED', 'UNQUALIFIED']).optional(),
  experianStatus: z.enum(['QUALIFIED', 'UNQUALIFIED']).optional(),
  firstName: z.string(),
  state: z.enum(['accepted', 'rejected']),
  status: apiStatusSchema,
  debtAmount: z.number().optional(),
  debtType: z.enum(['debt', 'debt2', 'none']).optional()
});

export const submitApplicationResponseSchema = z.object({
  status: apiStatusSchema,
  storeUuid: z.string(),
  offerId: z.string(),
  offerCount: z.number()
});

export const contactCenterAvailabilityResponseSchema = z.object({
  status: apiStatusSchema,
  capacity: z.number()
});

export const scheduleAppointmentResponseSchema = z.object({
  status: apiStatusSchema
});

export const connectCallResponseSchema = z.object({
  status: apiStatusSchema,
  from: z.string().optional()
});

export const timeslotAvailabilitySchema = z.object({
  booked: z.number(),
  capacity: z.number(),
  isAvailable: z.boolean(),
  slotsAvailable: z.number()
});

export const availabilityResponseSchema = z.object({
  status: apiStatusSchema,
  totalCalls: z.number(),
  data: z.record(z.string(), timeslotAvailabilitySchema)
});

// Prospect identity verification response
export const prospectVerifyResponseSchema = z.object({
  status: apiStatusSchema,
  prospectStatus: z.enum(['QUALIFIED', 'UNQUALIFIED', 'UNVERIFIED', 'NOT FOUND', 'ERROR']),
  debtAmount: z.number().optional(),
  firstName: z.string().optional(),
  verifiedVia: z.string().optional()
});

// New: Thank You (Success) tracking response
export const successPageResponseSchema = z.object({
  status: apiStatusSchema
});

// KBA (Knowledge-Based Authentication) schemas
export const kbaOptionSchema = z.object({
  id: z.string(),
  text: z.string()
});

export const kbaQuestionSchema = z.object({
  id: z.string(),
  question: z.string(),
  options: z.array(kbaOptionSchema)
});

export const connectKbaResponseSchema = z.object({
  status: apiStatusSchema,
  spinwheelId: z.string().optional(),
  hubspotRecordId: z.string().optional(),
  kbaQuestions: z.array(kbaQuestionSchema).optional(),
  connectionStatus: z.string().optional()
});

export const verifyKbaResponseSchema = z.object({
  status: apiStatusSchema,
  kbaQuestions: z.array(kbaQuestionSchema).optional(),
  connectionStatus: z.string().optional()
});

export type ApiStatus = z.infer<typeof apiStatusSchema>;
export type ConnectResponse = z.infer<typeof connectResponseSchema>;
export type CreateContactResponse = z.infer<typeof createContactResponseSchema>;
export type SubmitApplicationResponse = z.infer<typeof submitApplicationResponseSchema>;
export type ContactCenterAvailabilityResponse = z.infer<typeof contactCenterAvailabilityResponseSchema>;
export type ScheduleAppointmentResponse = z.infer<typeof scheduleAppointmentResponseSchema>;
export type ConnectCallResponse = z.infer<typeof connectCallResponseSchema>;
export type TimeslotAvailability = z.infer<typeof timeslotAvailabilitySchema>;
export type AvailabilityResponse = z.infer<typeof availabilityResponseSchema>;
export type ProspectVerifyResponse = z.infer<typeof prospectVerifyResponseSchema>;
export type SuccessPageResponse = z.infer<typeof successPageResponseSchema>;
export type KbaOption = z.infer<typeof kbaOptionSchema>;
export type KbaQuestion = z.infer<typeof kbaQuestionSchema>;
export type ConnectKbaResponse = z.infer<typeof connectKbaResponseSchema>;
export type VerifyKbaResponse = z.infer<typeof verifyKbaResponseSchema>;

// API Error
export class ApiError extends Error {
  constructor(
    message: string,
    public status: ApiStatus
  ) {
    super(message);
    this.name = 'ApiError';
  }
}